import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { helpdeskArticlesAPI, helpdeskAgentsAPI, kbCategoriesAPI } from '../../lib/api';
import KBCategoryManagerDialog from '../../components/helpdesk/KBCategoryManagerDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { RichPasteArea, sanitizeHTML } from '../../components/RichPasteArea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Plus,
  Edit,
  Search,
  BookOpen,
  Tag,
  User,
  Calendar,
  EyeOff,
  X,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Download,
  Trash2,
  Upload,
  Loader2,
  FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, cn } from '../../lib/utils';

// Color name -> Tailwind classes. Keep in sync with backend ALLOWED_KB_COLORS.
const KB_COLOR_MAP = {
  blue:    { bg: 'bg-blue-100',    fg: 'text-blue-700',    sw: 'bg-blue-500' },
  emerald: { bg: 'bg-emerald-100', fg: 'text-emerald-700', sw: 'bg-emerald-500' },
  purple:  { bg: 'bg-purple-100',  fg: 'text-purple-700',  sw: 'bg-purple-500' },
  amber:   { bg: 'bg-amber-100',   fg: 'text-amber-700',   sw: 'bg-amber-500' },
  rose:    { bg: 'bg-rose-100',    fg: 'text-rose-700',    sw: 'bg-rose-500' },
  indigo:  { bg: 'bg-indigo-100',  fg: 'text-indigo-700',  sw: 'bg-indigo-500' },
  teal:    { bg: 'bg-teal-100',    fg: 'text-teal-700',    sw: 'bg-teal-500' },
  lime:    { bg: 'bg-lime-100',    fg: 'text-lime-700',    sw: 'bg-lime-500' },
  orange:  { bg: 'bg-orange-100',  fg: 'text-orange-700',  sw: 'bg-orange-500' },
  slate:   { bg: 'bg-slate-100',   fg: 'text-slate-700',   sw: 'bg-slate-500' },
  cyan:    { bg: 'bg-cyan-100',    fg: 'text-cyan-700',    sw: 'bg-cyan-500' },
  pink:    { bg: 'bg-pink-100',    fg: 'text-pink-700',    sw: 'bg-pink-500' },
};
const KB_COLOR_OPTIONS = Object.keys(KB_COLOR_MAP);

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return ImageIcon;
  return FileText;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const KnowledgeBase = () => {
  const { isAdmin, token } = useAuth();
  const { canWrite: canWritePerm } = usePermissions();
  const canWriteKB = isAdmin() || canWritePerm('base_de_conocimiento');
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [userRole, setUserRole] = useState('helpdesk_requester');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    category: 'General',
    tags: [],
    is_published: false,
  });
  const [newTag, setNewTag] = useState('');
  
  // Attachments
  const [uploading, setUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const fileInputRef = useRef(null);
  
  // View article
  const [viewingArticle, setViewingArticle] = useState(null);
  const [viewSheetOpen, setViewSheetOpen] = useState(false);

  // Dynamic categories (Fase 4)
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const getCategoryColor = (value) => {
    const cat = categories.find(c => c.value === value);
    const cls = KB_COLOR_MAP[cat?.color] || KB_COLOR_MAP.slate;
    return `${cls.bg} ${cls.fg}`;
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await kbCategoriesAPI.getAll();
      setCategories(res.data || []);
    } catch (e) {
      console.error('Error fetching KB categories', e);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        helpdeskArticlesAPI.getAll(),
        helpdeskAgentsAPI.getUserRole(),
      ]);
      
      const [articlesResult, roleResult] = results;
      
      if (articlesResult.status === 'fulfilled') {
        setArticles(articlesResult.value.data);
      } else {
        console.error('Error fetching articles:', articlesResult.reason);
      }
      
      if (roleResult.status === 'fulfilled') {
        setUserRole(roleResult.value.data.role);
      } else {
        console.error('Error fetching user role:', roleResult.reason);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fase 3: visible para Admin O para usuarios con permiso 'full' en base_de_conocimiento.
  const canEdit = canWriteKB;

  const handleOpenDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title,
        content: article.content,
        category: article.category || 'General',
        tags: article.tags || [],
        is_published: article.is_published,
      });
      setPendingAttachments([]);
    } else {
      setEditingArticle(null);
      setArticleForm({ title: '', content: '', category: 'General', tags: [], is_published: false });
      setPendingAttachments([]);
    }
    setIsDialogOpen(true);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !articleForm.tags.includes(newTag.trim())) {
      setArticleForm({
        ...articleForm,
        tags: [...articleForm.tags, newTag.trim()],
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setArticleForm({
      ...articleForm,
      tags: articleForm.tags.filter(t => t !== tagToRemove),
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // If editing existing article, upload immediately
    if (editingArticle) {
      for (const file of files) {
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('file', file);
          await helpdeskArticlesAPI.uploadAttachment(editingArticle.id, formData);
          toast.success(`Archivo "${file.name}" subido`);
          
          // Refresh article
          const res = await helpdeskArticlesAPI.getById(editingArticle.id);
          setEditingArticle(res.data);
        } catch (error) {
          console.error('Error uploading:', error);
          toast.error(error.response?.data?.detail || `Error al subir "${file.name}"`);
        } finally {
          setUploading(false);
        }
      }
    } else {
      // For new articles, queue files
      setPendingAttachments([...pendingAttachments, ...files]);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePendingAttachment = (index) => {
    setPendingAttachments(pendingAttachments.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!editingArticle) return;
    
    try {
      await helpdeskArticlesAPI.deleteAttachment(editingArticle.id, attachmentId);
      toast.success('Archivo eliminado');
      
      // Refresh article
      const res = await helpdeskArticlesAPI.getById(editingArticle.id);
      setEditingArticle(res.data);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error al eliminar el archivo');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    const contentText = articleForm.content.replace(/<[^>]*>/g, '').trim();
    if (!articleForm.title.trim() || !contentText) {
      toast.error('Por favor completa el título y contenido');
      return;
    }
    
    setSaving(true);
    try {
      let savedArticle;
      
      if (editingArticle) {
        await helpdeskArticlesAPI.update(editingArticle.id, articleForm);
        toast.success('Artículo actualizado');
      } else {
        // Create article first
        const res = await helpdeskArticlesAPI.create(articleForm);
        savedArticle = res.data;
        
        // Upload pending attachments
        for (const file of pendingAttachments) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            await helpdeskArticlesAPI.uploadAttachment(savedArticle.id, formData);
          } catch (uploadError) {
            console.error('Error uploading attachment:', uploadError);
            toast.error(`Error al subir "${file.name}"`);
          }
        }
        
        toast.success('Artículo creado');
      }
      
      setIsDialogOpen(false);
      setPendingAttachments([]);
      fetchData();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el artículo');
    } finally {
      setSaving(false);
    }
  };

  const handleViewArticle = async (articleId) => {
    try {
      const res = await helpdeskArticlesAPI.getById(articleId);
      setViewingArticle(res.data);
      setViewSheetOpen(true);
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('Error al cargar el artículo');
    }
  };

  const getAttachmentDownloadUrl = (articleId, attachmentId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/helpdesk/articles/${articleId}/attachments/${attachmentId}`;
  };

  const handleDownloadAttachment = async (articleId, attachmentId, filename) => {
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${baseUrl}/api/helpdesk/articles/${articleId}/attachments/${attachmentId}`;
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error al descargar el archivo');
    }
  };

  const [attachmentBlobUrls, setAttachmentBlobUrls] = useState({});

  const loadAuthImage = useCallback(async (articleId, attachmentId) => {
    const key = `${articleId}_${attachmentId}`;
    if (attachmentBlobUrls[key]) return;
    try {
      const url = getAttachmentDownloadUrl(articleId, attachmentId);
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setAttachmentBlobUrls(prev => ({ ...prev, [key]: blobUrl }));
    } catch {}
  }, [attachmentBlobUrls]);

  // Filter articles
  const filteredArticles = articles.filter(article => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        article.title.toLowerCase().includes(query) ||
        article.content.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    if (categoryFilter && article.category !== categoryFilter) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-12 w-full" />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-40" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-800">
              Base de Conocimiento
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Instructivos y guías de uso de la plataforma
            </p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setManageOpen(true)}
                data-testid="manage-kb-categories-btn"
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Gestionar categorías
              </Button>
              <Button onClick={() => handleOpenDialog()} data-testid="create-article-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Instructivo
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar instructivos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <FolderOpen className="w-4 h-4 text-slate-400" />
                <button
                  onClick={() => setCategoryFilter('')}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-full transition-colors font-medium",
                    !categoryFilter 
                      ? "bg-slate-800 text-white" 
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  )}
                >
                  Todas
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setCategoryFilter(cat.value)}
                    className={cn(
                      "px-3 py-1.5 text-xs rounded-full transition-colors font-medium",
                      categoryFilter === cat.value 
                        ? "bg-slate-800 text-white" 
                        : getCategoryColor(cat.value) + " hover:opacity-80"
                    )}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No hay instructivos</p>
                <p className="text-sm text-slate-400 mt-1">
                  {searchQuery || categoryFilter
                    ? 'No se encontraron instructivos con los filtros aplicados'
                    : 'Crea el primer instructivo para la base de conocimiento'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredArticles.map(article => (
              <Card 
                key={article.id}
                className={cn(
                  "cursor-pointer hover:shadow-md transition-shadow",
                  !article.is_published && "opacity-60 border-dashed"
                )}
                onClick={() => handleViewArticle(article.id)}
                data-testid={`article-card-${article.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-medium line-clamp-2">
                      {article.title}
                    </CardTitle>
                    {!article.is_published && (
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Borrador
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-2">
                    <Badge className={cn("text-xs", getCategoryColor(article.category))}>
                      {article.category || 'General'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-3 mb-3">
                    {(article.content || '').replace(/<[^>]*>/g, '').substring(0, 150)}...
                  </p>
                  {article.attachments?.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-slate-400 mb-2">
                      <Paperclip className="w-3 h-3" />
                      {article.attachments.length} archivo(s)
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {article.created_by_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(article.created_at.split('T')[0])}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {editingArticle ? 'Editar Instructivo' : 'Nuevo Instructivo'}
              </DialogTitle>
              <DialogDescription>
                {editingArticle ? 'Modifica el contenido del instructivo' : 'Crea un nuevo instructivo para la base de conocimiento'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    placeholder="Título del instructivo"
                    value={articleForm.title}
                    onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                    data-testid="article-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="category">Categoría *</Label>
                  <Select
                    value={articleForm.category}
                    onValueChange={(v) => setArticleForm({ ...articleForm, category: v })}
                  >
                    <SelectTrigger data-testid="article-category-select">
                      <SelectValue placeholder="Selecciona una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="content">Contenido *</Label>
                  <RichPasteArea
                    value={articleForm.content}
                    onChange={(html) => setArticleForm({ ...articleForm, content: html })}
                    placeholder="Escribe el contenido del instructivo..."
                    className="min-h-[200px]"
                    data-testid="article-content-input"
                  />
                  <p className="text-xs text-slate-400">
                    Puedes pegar texto con negritas desde Word u otras aplicaciones
                  </p>
                </div>
                
                {/* Attachments Section */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Archivos Adjuntos
                  </Label>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  />
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Seleccionar archivos
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400">
                    Imágenes, PDF, Word, Excel, PowerPoint (máx. 10MB por archivo)
                  </p>
                  
                  {/* Pending attachments (new article) */}
                  {pendingAttachments.length > 0 && (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-slate-500 font-medium">Archivos pendientes:</p>
                      {pendingAttachments.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-sm flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-slate-400">{formatFileSize(file.size)}</span>
                          <button
                            type="button"
                            onClick={() => handleRemovePendingAttachment(index)}
                            className="text-slate-400 hover:text-rose-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Existing attachments (edit mode) */}
                  {editingArticle?.attachments?.length > 0 && (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-slate-500 font-medium">Archivos actuales:</p>
                      {editingArticle.attachments.map((att) => {
                        const FileIcon = getFileIcon(att.content_type);
                        return (
                          <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                            <FileIcon className="w-4 h-4 text-slate-400" />
                            <span className="text-sm flex-1 truncate">{att.original_filename}</span>
                            <span className="text-xs text-slate-400">{formatFileSize(att.size)}</span>
                            <button
                              type="button"
                              onClick={() => handleDeleteAttachment(att.id)}
                              className="text-slate-400 hover:text-rose-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Etiquetas adicionales</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Agregar etiqueta"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    />
                    <Button type="button" variant="outline" onClick={handleAddTag}>
                      Agregar
                    </Button>
                  </div>
                  {articleForm.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {articleForm.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-1 hover:text-rose-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    id="published"
                    checked={articleForm.is_published}
                    onCheckedChange={(checked) => setArticleForm({ ...articleForm, is_published: checked })}
                  />
                  <Label htmlFor="published" className="cursor-pointer">
                    Publicar instructivo (visible para todos los usuarios)
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-article-btn">
                  {saving ? 'Guardando...' : editingArticle ? 'Actualizar' : 'Crear'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Article Sheet */}
        <Sheet open={viewSheetOpen} onOpenChange={setViewSheetOpen}>
          <SheetContent className="sm:max-w-xl">
            {viewingArticle && (
              <>
                <SheetHeader>
                  <SheetTitle className="font-['Manrope'] text-lg pr-8">
                    {viewingArticle.title}
                  </SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge className={cn("text-xs", getCategoryColor(viewingArticle.category))}>
                        {viewingArticle.category || 'General'}
                      </Badge>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {viewingArticle.created_by_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(viewingArticle.created_at.split('T')[0])}
                      </span>
                      {!viewingArticle.is_published && (
                        <Badge variant="outline" className="text-xs">
                          <EyeOff className="w-3 h-3 mr-1" />
                          Borrador
                        </Badge>
                      )}
                    </div>
                  </SheetDescription>
                </SheetHeader>
                <ScrollArea className="h-[calc(100vh-200px)] mt-6">
                  <div className="pr-4">
                    {viewingArticle.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {viewingArticle.tags.map(tag => (
                          <Badge key={tag} variant="secondary">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <div className="prose prose-sm prose-slate max-w-none">
                      <div
                        className="whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(viewingArticle.content || '') }}
                      />
                    </div>
                    
                    {/* Attachments */}
                    {viewingArticle.attachments?.length > 0 && (
                      <div className="mt-6 pt-4 border-t">
                        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                          <Paperclip className="w-4 h-4" />
                          Archivos adjuntos
                        </h4>
                        <div className="space-y-2">
                          {viewingArticle.attachments.map((att) => {
                            const FileIcon = getFileIcon(att.content_type);
                            const isImage = att.content_type?.startsWith('image/');
                            const blobKey = `${viewingArticle.id}_${att.id}`;
                            const blobSrc = attachmentBlobUrls[blobKey];
                            
                            if (isImage && !blobSrc) {
                              loadAuthImage(viewingArticle.id, att.id);
                            }
                            
                            return (
                              <div key={att.id} className="border rounded-lg overflow-hidden">
                                {isImage && blobSrc && (
                                  <div className="p-2 bg-slate-50">
                                    <img 
                                      src={blobSrc}
                                      alt={att.original_filename}
                                      className="max-w-full h-auto rounded"
                                      style={{ maxHeight: '300px' }}
                                    />
                                  </div>
                                )}
                                <div className="flex items-center gap-2 p-3 bg-white">
                                  <FileIcon className="w-5 h-5 text-slate-400" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{att.original_filename}</p>
                                    <p className="text-xs text-slate-400">{formatFileSize(att.size)}</p>
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDownloadAttachment(viewingArticle.id, att.id, att.original_filename); }}
                                    className="p-2 text-slate-400 hover:text-blue-500 transition-colors"
                                    data-testid={`download-attachment-${att.id}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {canEdit && (
                      <div className="mt-6 pt-4 border-t">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setViewSheetOpen(false);
                            handleOpenDialog(viewingArticle);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Editar instructivo
                        </Button>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* KB Categories Manager */}
        <KBCategoryManagerDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          categories={categories}
          onChanged={setCategories}
        />
      </div>
    </Layout>
  );
};

export default KnowledgeBase;
